import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  OnDestroy,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ComplaintReasonRecord,
  ComplaintReasonService,
  ComplaintReasonVisibleType,
  ComplaintType,
} from '@libs/shared/api';
import { Observable } from 'rxjs';
import { allAllowInElements } from '@libs/shared/utils/const/shortcut';
import { injectHotkeyManagerService } from '@libs/shared/hotkey/hotkey-manager.service';

export interface ComplaintDialogResultData {
  reasonIds: string[];
  description: string;
}

@Component({
  selector: 'app-complaint-dialog',
  template: `
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <div class="text-2xl font-bold">신고 생성하기</div>
        <div class="text-secondary text-base">
          아래에서 신고 사유를 선택해주세요. (다중선택 가능)
        </div>
      </div>
      <div mat-dialog-content>
        <form
          [formGroup]="form"
          (keydown.enter)="$event.preventDefault()"
          class="flex max-w-screen-sm flex-col gap-2">
          <app-chip-group>
            @for (reason of complaintReasons$ | async; track reason) {
              <app-chip
                [selected]="selectedReasonIds.includes(reason.id)"
                (click)="onToggleReason(reason)"
                mat-ripple
                color="gray"
                size="lg">
                {{ reason.description }}
              </app-chip>
            }
          </app-chip-group>
          <mat-form-field
            [floatLabel]="'always'"
            class="fuse-mat-no-header w-full"
            appearance="fill">
            <textarea
              matInput
              formControlName="description"
              placeholder="추가 메시지 (Shift+Enter: 줄바꿈)">
            </textarea>
          </mat-form-field>
        </form>
      </div>
    </div>
    <div mat-dialog-actions class="flex flex-row justify-end">
      <button mat-stroked-button (click)="onClose()">취소</button>
      <button
        mat-flat-button
        color="warn"
        [disabled]="form.invalid"
        [matDialogClose]="{
          reasonIds: selectedReasonIds,
          description: form.get('description').value
        }"
        (click)="onSubmit()">
        제출
      </button>
    </div>
    `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplaintDialogComponent implements AfterViewInit, OnDestroy {
  readonly complaintReasonService = inject(ComplaintReasonService);
  readonly hotkeyManager = injectHotkeyManagerService();

  form = this._fb.group({
    description: [null],
  });
  selectedReasonIds: string[] = [];
  complaintReasons$: Observable<ComplaintReasonRecord[]>;

  constructor(
    private _fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      complaintType: ComplaintType;
      visibleType: ComplaintReasonVisibleType;
    },
    public matDialogRef: MatDialogRef<
      ComplaintDialogComponent,
      ComplaintDialogResultData
    >
  ) {
    this.complaintReasons$ = this.complaintReasonService.fetchComplaintReasons(
      true,
      data.complaintType,
      data.visibleType
    );
  }

  ngAfterViewInit(): void {
    this.hotkeyManager.activateHotkeysForComponent(this.constructor.name, [
      {
        keys: 'meta.enter',
        description: '저장하기',
        allowIn: allAllowInElements,
        action: () => {
          this.onSubmit();
        },
      },
      {
        keys: 'escape',
        description: '닫기',
        allowIn: allAllowInElements,
        action: () => {
          this.onClose();
        },
      },
    ]);
  }

  ngOnDestroy(): void {
    this.hotkeyManager.deactivateHotkeys(this.constructor.name);
  }

  onSubmit() {
    if (this.form.valid) {
      this.matDialogRef.close({
        reasonIds: this.selectedReasonIds,
        description: this.form.get('description').value,
      });
    }
  }

  onClose() {
    this.matDialogRef.close();
  }

  onToggleReason(reason: ComplaintReasonRecord) {
    if (this.selectedReasonIds.includes(reason.id)) {
      this.selectedReasonIds = this.selectedReasonIds.filter(
        id => id !== reason.id
      );
    } else {
      this.selectedReasonIds.push(reason.id);
    }
  }
}
